import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styles from "../DatePicker.module.css";
import { Calendar } from "lucide-react";
import { createPortal } from "react-dom";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DateRangePicker = ({
  startDate,
  endDate,
  onRangeSelect,
  placeholder = "Select date range",
  disableFutureDates = true,
  disabled = false,
}) => {
  const today = new Date();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(
    startDate?.getMonth() ?? today.getMonth()
  );
  const [currentYear, setCurrentYear] = useState(
    startDate?.getFullYear() ?? today.getFullYear()
  );
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [monthSearch, setMonthSearch] = useState("");
  const [yearSearch, setYearSearch] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 240,
    placement: "bottom",
  });
  const [rangeStart, setRangeStart] = useState(startDate || null);
  const [rangeEnd, setRangeEnd] = useState(endDate || null);
  const [hoverDate, setHoverDate] = useState(null);

  const containerRef = useRef(null);
  const monthDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const calendarRef = useRef(null);

  const currentYearValue = new Date().getFullYear();
  const years = Array.from(
    { length: currentYearValue - 1899 + 10 },
    (_, i) => currentYearValue + 10 - i
  );
  const filteredMonths = MONTHS.filter((month) =>
    month.toLowerCase().includes(monthSearch.toLowerCase())
  );
  const filteredYears = years.filter((year) =>
    year.toString().includes(yearSearch)
  );

  useEffect(() => {
    if (startDate) {
      setCurrentMonth(startDate.getMonth());
      setCurrentYear(startDate.getFullYear());
      setRangeStart(startDate);
    } else {
      // Parent cleared the range (e.g. a preset filter was picked) — drop the
      // stale internal selection so the button reverts to the placeholder.
      setRangeStart(null);
    }
    if (endDate) {
      setRangeEnd(endDate);
    } else {
      setRangeEnd(null);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        (containerRef.current && containerRef.current.contains(e.target)) ||
        (calendarRef.current && calendarRef.current.contains(e.target))
      ) {
        return;
      }
      setIsOpen(false);
      setShowMonthDropdown(false);
      setShowYearDropdown(false);
      setMonthSearch("");
      setYearSearch("");
      setHoverDate(null);
    };
    const handleEscapeKey = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
        setMonthSearch("");
        setYearSearch("");
        setHoverDate(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setShowMonthDropdown(false);
      setShowYearDropdown(false);
      setMonthSearch("");
      setYearSearch("");
      setHoverDate(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (showMonthDropdown && monthDropdownRef.current) {
      const currentMonthButton = monthDropdownRef.current.querySelector(
        `[data-month="${currentMonth}"]`
      );
      currentMonthButton?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [showMonthDropdown, currentMonth]);

  useEffect(() => {
    if (showYearDropdown && yearDropdownRef.current) {
      const currentYearButton = yearDropdownRef.current.querySelector(
        `[data-year="${currentYear}"]`
      );
      currentYearButton?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [showYearDropdown, currentYear]);

  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = Array(firstDay)
      .fill(null)
      .concat([...Array(daysInMonth).keys()].map((d) => d + 1));
    return days;
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentYear, currentMonth, day, 12);
    if (!rangeStart || (rangeStart && rangeEnd) || clickedDate < rangeStart) {
      setRangeStart(clickedDate);
      setRangeEnd(null);
      setHoverDate(null);
    } else {
      setRangeEnd(clickedDate);
      setIsOpen(false);
      setHoverDate(null);
      if (onRangeSelect) {
        onRangeSelect(rangeStart, clickedDate);
      }
    }
  };

  const handleMonthSelect = (index) => {
    setCurrentMonth(index);
    setShowMonthDropdown(false);
    setMonthSearch("");
  };

  const handleYearSelect = (year) => {
    setCurrentYear(year);
    setShowYearDropdown(false);
    setYearSearch("");
  };

  const navigateMonth = (dir) => {
    if (dir === "prev") {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const formatRange = (start, end) => {
    if (!start && !end) return placeholder;
    if (start && !end)
      return `${start.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })} - ...`;
    if (start && end)
      return `${start.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })} - ${end.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })}`;
    return placeholder;
  };

  const calendarDays = generateCalendarDays();

  const isTodayDate = (day) =>
    day === today.getDate() &&
    currentMonth === today.getMonth() &&
    currentYear === today.getFullYear();

  const isSelected = (day) => {
    const date = new Date(currentYear, currentMonth, day, 12);
    return (
      (rangeStart && date.toDateString() === rangeStart.toDateString()) ||
      (rangeEnd && date.toDateString() === rangeEnd.toDateString())
    );
  };

  const isInRange = (day) => {
    if (!rangeStart) return false;
    const date = new Date(currentYear, currentMonth, day, 12);
    if (rangeStart && !rangeEnd && hoverDate) {
      // Highlight between start and hover
      return (
        (date > rangeStart && date <= hoverDate) ||
        (date < rangeStart && date >= hoverDate)
      );
    }
    if (rangeStart && rangeEnd) {
      return date > rangeStart && date < rangeEnd;
    }
    return false;
  };

  const isFutureDate = (day) => {
    if (!day) return false;
    const date = new Date(currentYear, currentMonth, day);
    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const openDropdown = () => {
    if (disabled) return; // locked while a report is generated (SCRUM-191)
    if (!isOpen) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = 280;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let placement = "bottom";
      let top = rect.bottom + window.scrollY + 2;
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        placement = "top";
        top = rect.top + window.scrollY - dropdownHeight - 2;
      }
      setDropdownPosition({
        top,
        left: rect.left + window.scrollX,
        width: rect.width,
        placement,
      });
    }
    setIsOpen((prev) => !prev);
  };

  // Utility to find the closest scrollable parent
  function getScrollableParent(node) {
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        return node;
      }
      node = node.parentElement;
    }
    // If no scrollable parent found, use document.body
    return document.body;
  }

  useEffect(() => {
    let scrollableParent = null;
    let prevOverflow = null;
    if (isOpen && containerRef.current) {
      scrollableParent = getScrollableParent(containerRef.current);
      if (scrollableParent) {
        prevOverflow = scrollableParent.style.overflowY;
        scrollableParent.style.overflowY = "hidden";
      }
    }
    return () => {
      if (scrollableParent) {
        scrollableParent.style.overflowY = prevOverflow || "";
      }
    };
  }, [isOpen]);

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        onClick={openDropdown}
        className={styles.inputButton}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        disabled={disabled}
        style={
          disabled ? { opacity: 0.55, cursor: "not-allowed" } : undefined
        }
      >
        <Calendar size={12} className={styles.dateIcon} />
        <span
          className={
            rangeStart || rangeEnd ? styles.inputText : styles.inputPlaceholder
          }
        >
          {formatRange(rangeStart, rangeEnd)}
        </span>
      </button>

      {isOpen &&
        createPortal(
          <div
            className={`${styles.dropdown} ${styles.dropdownOpen}`}
            ref={calendarRef}
            role="dialog"
            aria-label="Date range picker"
            style={{
              position: "absolute",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: "240px",
              zIndex: 9999,
            }}
          >
            <div className={styles.header}>
              <button
                type="button"
                onClick={() => navigateMonth("prev")}
                className={styles.navBtn}
                aria-label="Previous month"
              >
                <ChevronLeft size={12} />
              </button>
              <div className={styles.monthYear}>
                <div className={styles.dropdownWrap}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMonthDropdown(!showMonthDropdown);
                      setShowYearDropdown(false);
                    }}
                    className={`${styles.monthButton} ${
                      showMonthDropdown ? styles.activeDropdown : ""
                    }`}
                    aria-expanded={showMonthDropdown}
                  >
                    {MONTHS[currentMonth].substring(0, 3)}
                  </button>
                  {showMonthDropdown && (
                    <div className={styles.dropdownBox} ref={monthDropdownRef}>
                      <div className={styles.searchWrap}>
                        <input
                          value={monthSearch}
                          onChange={(e) => setMonthSearch(e.target.value)}
                          className={styles.searchInput}
                          placeholder="Search "
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className={styles.scrollList}>
                        {filteredMonths.map((month) => {
                          const index = MONTHS.indexOf(month);
                          return (
                            <button
                              type="button"
                              key={month}
                              data-month={index}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMonthSelect(index);
                              }}
                              className={`${styles.dropdownItem} ${
                                index === currentMonth ? styles.activeItem : ""
                              }`}
                            >
                              {month}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.dropdownWrap}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowYearDropdown(!showYearDropdown);
                      setShowMonthDropdown(false);
                    }}
                    className={`${styles.yearButton} ${
                      showYearDropdown ? styles.activeDropdown : ""
                    }`}
                    aria-expanded={showYearDropdown}
                  >
                    {currentYear}
                  </button>
                  {showYearDropdown && (
                    <div className={styles.dropdownBox} ref={yearDropdownRef}>
                      <div className={styles.searchWrap}>
                        <input
                          value={yearSearch}
                          onChange={(e) => setYearSearch(e.target.value)}
                          className={styles.searchInput}
                          placeholder="Search "
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className={styles.scrollList}>
                        {filteredYears.map((year) => (
                          <button
                            type="button"
                            key={year}
                            data-year={year}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleYearSelect(year);
                            }}
                            className={`${styles.dropdownItem} ${
                              year === currentYear ? styles.activeItem : ""
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigateMonth("next")}
                className={styles.navBtn}
                aria-label="Next month"
              >
                <ChevronRight size={12} />
              </button>
            </div>
            <div className={styles.calendar}>
              <div className={styles.weekRow}>
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <div key={day} className={styles.weekDay}>
                    {day}
                  </div>
                ))}
              </div>
              <div className={styles.daysGrid}>
                {calendarDays.map((day, i) => (
                  <div key={i} className={styles.dayCell}>
                    {day && (
                      <button
                        type="button"
                        onClick={() =>
                          !disableFutureDates || !isFutureDate(day)
                            ? handleDateClick(day)
                            : undefined
                        }
                        className={`${styles.dayButton} ${
                          isSelected(day)
                            ? styles.selectedDay
                            : isTodayDate(day)
                            ? styles.today
                            : ""
                        } ${isInRange(day) ? styles.inRange : ""} ${
                          disableFutureDates && isFutureDate(day)
                            ? styles.disabledDay
                            : ""
                        }`}
                        aria-label={`Select ${day} ${MONTHS[currentMonth]} ${currentYear}`}
                        disabled={disableFutureDates && isFutureDate(day)}
                        onMouseEnter={() => {
                          if (rangeStart && !rangeEnd) {
                            setHoverDate(
                              new Date(currentYear, currentMonth, day, 12)
                            );
                          }
                        }}
                        onMouseLeave={() => {
                          if (rangeStart && !rangeEnd) {
                            setHoverDate(null);
                          }
                        }}
                      >
                        {day}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DateRangePicker;
